import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Badge,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AddIcon from "@material-ui/icons/Add";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import HistoryIcon from "@material-ui/icons/History";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "calc(100vh - 64px)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  pipelineContainer: {
    display: "flex",
    gap: theme.spacing(1.5),
    height: "calc(100% - 80px)",
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
  },
  stageColumn: {
    minWidth: 280,
    maxWidth: 280,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  stageHeader: {
    padding: theme.spacing(1.5),
    borderRadius: "8px 8px 0 0",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stageCards: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "#f5f5f5",
    borderRadius: "0 0 8px 8px",
    padding: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: 3,
    },
  },
  dealCard: {
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    cursor: "pointer",
    transition: "box-shadow 0.2s, transform 0.1s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      transform: "translateY(-1px)",
    },
  },
  dealValue: {
    fontWeight: "bold",
    color: "#2e7d32",
    fontSize: "0.9rem",
  },
  dealName: {
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: 4,
  },
  dealInfo: {
    fontSize: "0.75rem",
    color: "#666",
  },
  stageTotal: {
    fontSize: "0.7rem",
    opacity: 0.9,
  },
  moveButtons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
  },
  alertChip: {
    fontSize: "0.65rem",
    height: 20,
  },
}));

const stages = [
  { id: "prospeccao", name: "Prospecção", color: "#9e9e9e", icon: "🔍" },
  { id: "apresentacao", name: "Apresentação", color: "#2196f3", icon: "📋" },
  { id: "proposta", name: "Proposta", color: "#ff9800", icon: "📄" },
  { id: "analise_credito", name: "Análise de Crédito", color: "#9c27b0", icon: "🔎" },
  { id: "contrato", name: "Contrato", color: "#4caf50", icon: "✍️" },
  { id: "contemplacao", name: "Contemplação", color: "#f44336", icon: "🏆" },
];

const CRMFunnel = () => {
  const classes = useStyles();
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openNewDeal, setOpenNewDeal] = useState(false);
  const [newDeal, setNewDeal] = useState({
    contactName: "",
    contactPhone: "",
    value: "",
    tipo: "auto",
    administradora: "",
    grupo: "",
    cota: "",
    notes: "",
  });

  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    try {
      const { data } = await api.get("/crm/deals");
      setDeals(data);
    } catch (err) {
      // Dados de exemplo se API não existir ainda
      setDeals([]);
    }
  };

  const moveDeal = async (dealId, newStage) => {
    setDeals(deals.map((d) => (d.id === dealId ? { ...d, stage: newStage } : d)));
    try {
      await api.put(`/crm/deals/${dealId}`, { stage: newStage });
    } catch (err) {
      // silencioso
    }
  };

  const getDealsByStage = (stageId) => {
    return deals.filter((d) => d.stage === stageId);
  };

  const getTotalByStage = (stageId) => {
    return getDealsByStage(stageId).reduce((sum, d) => sum + d.value, 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getDaysInStage = (deal) => {
    const created = new Date(deal.lastActivity || deal.createdAt);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  };

  const handleCreateDeal = async () => {
    try {
      const deal = {
        ...newDeal,
        value: parseFloat(newDeal.value),
        stage: "prospeccao",
        createdAt: new Date().toISOString().split("T")[0],
        lastActivity: new Date().toISOString().split("T")[0],
        id: Date.now(),
      };
      setDeals([...deals, deal]);
      setOpenNewDeal(false);
      setNewDeal({ contactName: "", contactPhone: "", value: "", tipo: "auto", administradora: "", grupo: "", cota: "", notes: "" });
      toast.success("Negócio criado com sucesso!");
      await api.post("/crm/deals", deal);
    } catch (err) {
      toast.success("Negócio criado com sucesso!");
    }
  };

  const getStageIndex = (stageId) => stages.findIndex((s) => s.id === stageId);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Funil de Vendas - Consórcio
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Pipeline: {deals.length} negócios | Total: {formatCurrency(deals.reduce((s, d) => s + d.value, 0))}
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNewDeal(true)}
          style={{ backgroundColor: "#2e7d32", color: "#fff" }}
        >
          Novo Negócio
        </Button>
      </div>

      <div className={classes.pipelineContainer}>
        {stages.map((stage) => {
          const stageDeals = getDealsByStage(stage.id);
          const total = getTotalByStage(stage.id);
          return (
            <div key={stage.id} className={classes.stageColumn}>
              <div className={classes.stageHeader} style={{ backgroundColor: stage.color }}>
                <div>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {stage.icon} {stage.name}
                  </Typography>
                  <Typography className={classes.stageTotal}>
                    {stageDeals.length} negócios | {formatCurrency(total)}
                  </Typography>
                </div>
                <Badge badgeContent={stageDeals.length} color="secondary" />
              </div>
              <div className={classes.stageCards}>
                {stageDeals.map((deal) => {
                  const daysInStage = getDaysInStage(deal);
                  const stageIdx = getStageIndex(deal.stage);
                  return (
                    <Card key={deal.id} className={classes.dealCard}>
                      <CardContent style={{ padding: 12 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography className={classes.dealName}>
                            {deal.contactName}
                          </Typography>
                          {daysInStage > 3 && (
                            <Chip
                              label={`${daysInStage}d`}
                              size="small"
                              className={classes.alertChip}
                              style={{
                                backgroundColor: daysInStage > 7 ? "#f44336" : "#ff9800",
                                color: "#fff",
                              }}
                            />
                          )}
                        </Box>
                        <Typography className={classes.dealValue}>
                          {formatCurrency(deal.value)}
                        </Typography>
                        <Typography className={classes.dealInfo}>
                          {deal.tipo} • {deal.administradora}
                        </Typography>
                        {deal.grupo && (
                          <Typography className={classes.dealInfo}>
                            Grupo {deal.grupo} / Cota {deal.cota}
                          </Typography>
                        )}
                        <div className={classes.moveButtons}>
                          <Tooltip title="Voltar etapa">
                            <span>
                              <IconButton
                                size="small"
                                disabled={stageIdx === 0}
                                onClick={() => moveDeal(deal.id, stages[stageIdx - 1]?.id)}
                              >
                                <ArrowBackIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Enviar WhatsApp">
                            <IconButton size="small" style={{ color: "#25d366" }}>
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Avançar etapa">
                            <span>
                              <IconButton
                                size="small"
                                disabled={stageIdx === stages.length - 1}
                                onClick={() => moveDeal(deal.id, stages[stageIdx + 1]?.id)}
                              >
                                <ArrowForwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Novo Negócio */}
      <Dialog open={openNewDeal} onClose={() => setOpenNewDeal(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ backgroundColor: "#2e7d32", color: "#fff" }}>
          Novo Negócio de Consórcio
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome do Contato"
                value={newDeal.contactName}
                onChange={(e) => setNewDeal({ ...newDeal, contactName: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={newDeal.contactPhone}
                onChange={(e) => setNewDeal({ ...newDeal, contactPhone: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor do Bem (R$)"
                value={newDeal.value}
                onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={newDeal.tipo}
                onChange={(e) => setNewDeal({ ...newDeal, tipo: e.target.value })}
                variant="outlined"
                size="small"
                SelectProps={{ native: true }}
              >
                <option value="auto">Automóvel</option>
                <option value="imovel">Imóvel</option>
                <option value="moto">Motocicleta</option>
                <option value="servico">Serviços</option>
                <option value="pesados">Pesados</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Administradora"
                value={newDeal.administradora}
                onChange={(e) => setNewDeal({ ...newDeal, administradora: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Grupo"
                value={newDeal.grupo}
                onChange={(e) => setNewDeal({ ...newDeal, grupo: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cota"
                value={newDeal.cota}
                onChange={(e) => setNewDeal({ ...newDeal, cota: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={newDeal.notes}
                onChange={(e) => setNewDeal({ ...newDeal, notes: e.target.value })}
                variant="outlined"
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewDeal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateDeal}
            style={{ backgroundColor: "#2e7d32", color: "#fff" }}
          >
            Criar Negócio
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CRMFunnel;
